<template>
  <div class="self-stretch flex flex-col gap-1 px-3 py-2 rounded bg-transparent outline outline-1 outline-border-separator"
       data-test="scheduled-activity-card">
    <div class="self-stretch inline-flex items-center gap-2">
      <i class="ri-time-line ri-100 text-text-subtle flex-shrink-0" aria-hidden="true" />
      <div class="flex-1 font-sans text-xs font-bold uppercase tracking-[0.06em] text-text-subtle" data-test="kind">
        {{ kind_label }}
      </div>
    </div>
    <div class="self-stretch font-sans text-sm text-text-subtle" data-test="notice">
      <slot />
    </div>
    <div v-if="deferred_label" class="self-stretch font-sans text-xs text-text-subtle" data-test="deferred-until">
      {{ deferred_label }}
    </div>
  </div>
</template>

<script>
/**
 * What a gate held back, in the Scheduled tasks thread.
 *
 * It is NOT a result card, and the difference is load-bearing rather than cosmetic. A held-back run
 * produced no answer, so it has no outcome pill, no data-as-of time and nothing to copy or give
 * feedback on — and the message carrying it is typed `activity`, which keeps it out of the unread
 * count and out of anything that reads the thread for results. Dressing it as a result would promise
 * all of that and deliver none of it.
 *
 * Quieter than a result card on purpose: an outline rather than a filled card, and the whole thing in
 * text-subtle. The reason someone reads it is that a result they expected is missing, so it has to be
 * findable and legible without competing with the results around it.
 *
 * The notice text itself arrives as the message body and is rendered through the default slot, so the
 * wording stays server-side where HeldResultNotice can keep the two kinds consistent — and so this
 * component never has to reproduce a sentence it would then have to keep in step.
 */
export default {
  name: 'ScheduledActivityCard',
  props: {
    /** HeldResultKind name: 'DailyCap' or 'QuietHours'. */
    kind: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * ISO-8601 UTC string of when a deferred occurrence will run. Empty for kinds that do not defer —
     * a capped day holds the result for tomorrow's brief rather than rescheduling it to a known moment.
     */
    deferredUntil: {
      type: String,
      required: false,
      default: '',
    },
  },
  computed: {
    /**
     * The band's label. Falls back to a generic one for a kind this client has not been taught, rather
     * than printing the raw enum name at a dealership manager — whatever held the run, "held back" is
     * still true of it.
     */
    kind_label() {
      switch (this.kind) {
        case 'QuietHours': return this.$t('messaging.scheduledActivity.quietHours');
        case 'DailyCap': return this.$t('messaging.scheduledActivity.dailyCap');
        default: return this.$t('messaging.scheduledActivity.heldBack');
      }
    },

    /**
     * When the deferred run will happen, in the VIEWER's zone via the browser's own formatter.
     *
     * The server deliberately keeps this out of the notice prose and passes the instant instead, so
     * there is exactly one answer to "when" and it is computed where the reader is. An unparseable or
     * absent value renders nothing rather than a guess.
     */
    deferred_label() {
      if (!this.deferredUntil) return '';
      const when = new Date(this.deferredUntil);
      if (Number.isNaN(when.getTime())) return '';

      try {
        return this.$t('messaging.scheduledActivity.willRunAt', {
          time: when.toLocaleString(undefined, {
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
          }),
        });
      } catch (e) {
        return '';
      }
    },
  },
};
</script>
